<template>
  <div class="image-asset-popup">
    <!-- Popup Header -->
    <div class="popup-header bg-gradient-primary text-white p-3">
      <div class="d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          <i class="material-icons-round me-2" style="vertical-align: middle;">image</i>
          교재 이미지 관리
        </h5>
        <button 
          type="button" 
          class="btn-close btn-close-white"
          @click="closeWindow"
          aria-label="Close"
        ></button>
      </div>
    </div>

    <!-- Main Content -->
    <div class="popup-content p-4">
      <!-- Barcode Display -->
      <div class="alert alert-info mb-4" v-if="barcode">
        <div class="d-flex align-items-center">
          <i class="material-icons-round me-2">qr_code</i>
          <div>
            <strong>교재 바코드:</strong> {{ barcode }}
          </div>
        </div>
      </div>

      <!-- Image Grid -->
      <div class="image-grid-container">
        <div v-if="isLoading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="text-muted mt-3">이미지를 불러오는 중...</p>
        </div>

        <div v-else-if="error" class="alert alert-danger">
          <i class="material-icons-round me-2">error</i>
          {{ error }}
        </div>

        <div v-else-if="images.length === 0" class="text-center py-5">
          <i class="material-icons-round text-secondary mb-3" style="font-size: 64px;">image_not_supported</i>
          <h6 class="text-secondary">등록된 이미지가 없습니다</h6>
          <p class="text-muted">이미지를 업로드하여 시작하세요</p>
        </div>

        <div v-else class="row g-3">
          <div 
            v-for="(image, index) in images" 
            :key="image.id || index"
            class="col-6 col-md-4 col-lg-3"
          >
            <div class="image-card">
              <div class="image-wrapper">
                <img 
                  :src="getImageUrl(image)" 
                  :alt="image.filename || 'Image'"
                  class="img-fluid"
                  @click="selectImage(image)"
                >
                <div class="image-overlay">
                  <button 
                    class="btn btn-sm btn-primary"
                    @click="selectImage(image)"
                  >
                    <i class="material-icons-round">check</i>
                    선택
                  </button>
                </div>
              </div>
              <div class="image-info p-2">
                <p class="mb-0 text-truncate small">{{ image.filename }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Upload Section -->
      <div class="upload-section mt-4">
        <div class="card border-dashed">
          <div class="card-body text-center py-4">
            <input 
              type="file"
              ref="fileInput"
              class="d-none"
              accept="image/*"
              multiple
              @change="handleFileUpload"
            >
            <i class="material-icons-round text-primary mb-3" style="font-size: 48px;">cloud_upload</i>
            <h6>이미지 업로드</h6>
            <p class="text-muted mb-3">클릭하거나 파일을 드래그하여 업로드하세요</p>
            <button 
              class="btn btn-primary"
              @click="$refs.fileInput.click()"
            >
              <i class="material-icons-round me-2">upload</i>
              파일 선택
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, getCurrentInstance } from 'vue'
import CONF from '@/Config.js'

export default {
  name: 'ImageAssetPopup',
  setup() {
    const instance = getCurrentInstance()
    const axios = instance?.proxy?.$axios

    // State
    const barcode = ref('')
    const images = ref([])
    const isLoading = ref(false)
    const error = ref('')

    // Get barcode from URL query
    onMounted(() => {
      const urlParams = new URLSearchParams(window.location.search)
      barcode.value = urlParams.get('barcode') || ''
      
      if (barcode.value) {
        loadImages()
      }
    })

    // Load images for the textbook
    const loadImages = async () => {
      isLoading.value = true
      error.value = ''
      
      try {
        const response = await axios({
          method: 'get',
          url: `/exam-files/images/${barcode.value}`,
          api_url: CONF.ACHIEVE_API_URL
        })
        
        if (response.status === 200) {
          images.value = response.data.items || []
        }
      } catch (err) {
        console.error('Failed to load images:', err)
        error.value = '이미지를 불러오는데 실패했습니다.'
        // 테스트용 더미 데이터
        images.value = [
          { id: 1, filename: 'image1.jpg', url: '' },
          { id: 2, filename: 'image2.jpg', url: '' },
          { id: 3, filename: 'image3.jpg', url: '' }
        ]
      } finally {
        isLoading.value = false
      }
    }

    // Get image URL
    const getImageUrl = (image) => {
      if (image.url) {
        return image.url
      }
      return `${CONF.ACHIEVE_API_URL}/files/image/stream/${image.filename}?barcode=${barcode.value}&file_type=exam_files`
    }

    // Select image and return to parent
    const selectImage = (image) => {
      // 부모 창에 이미지 정보 전달
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage({
          type: 'IMAGE_SELECTED',
          data: {
            filename: image.filename,
            url: getImageUrl(image),
            id: image.id
          }
        }, '*')
        
        // 팝업 닫기
        window.close()
      }
    }

    // Handle file upload
    const handleFileUpload = async (event) => {
      const files = event.target.files
      if (!files || files.length === 0) return

      const formData = new FormData()
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i])
      }
      formData.append('barcode', barcode.value)

      try {
        isLoading.value = true
        const response = await axios({
          method: 'post',
          url: '/exam-files/upload/images',
          api_url: CONF.ACHIEVE_API_URL,
          data: formData,
          loadingBar: true
        })

        if (response.status === 200) {
          // 업로드 성공 후 이미지 목록 새로고침
          await loadImages()
          
          if (instance?.proxy?.$notifyVue) {
            instance.proxy.$notifyVue({
              type: 'success',
              title: '업로드 성공',
              text: '이미지가 성공적으로 업로드되었습니다.'
            })
          }
        }
      } catch (err) {
        console.error('Upload failed:', err)
        if (instance?.proxy?.$notifyVue) {
          instance.proxy.$notifyVue({
            type: 'error',
            title: '업로드 실패',
            text: '이미지 업로드 중 오류가 발생했습니다.'
          })
        }
      } finally {
        isLoading.value = false
        // 파일 입력 초기화
        event.target.value = ''
      }
    }

    // Close window
    const closeWindow = () => {
      window.close()
    }

    return {
      barcode,
      images,
      isLoading,
      error,
      loadImages,
      getImageUrl,
      selectImage,
      handleFileUpload,
      closeWindow
    }
  }
}
</script>

<style scoped>
.image-asset-popup {
  min-height: 100vh;
  background: #f8f9fa;
}

.popup-header {
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.bg-gradient-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.popup-content {
  max-width: 1400px;
  margin: 0 auto;
}

.image-grid-container {
  min-height: 400px;
}

.image-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  transition: all 0.3s ease;
  cursor: pointer;
}

.image-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.12);
}

.image-wrapper {
  position: relative;
  padding-top: 75%; /* 4:3 Aspect Ratio */
  overflow: hidden;
  background: #f5f5f5;
}

.image-wrapper img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.image-card:hover .image-overlay {
  opacity: 1;
}

.image-info {
  background: white;
  border-top: 1px solid #e9ecef;
}

.upload-section .card {
  border: 2px dashed #dee2e6;
  background: #ffffff;
  transition: all 0.3s ease;
}

.upload-section .card:hover {
  border-color: #667eea;
  background: #f8f9ff;
}

.border-dashed {
  border-style: dashed !important;
}

/* Responsive */
@media (max-width: 768px) {
  .popup-content {
    padding: 1rem !important;
  }
  
  .col-6 {
    flex: 0 0 auto;
    width: 50%;
  }
}
</style>